import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { PartnerBoardEntry, Tournament } from "../types";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import SafeAvatar from "./SafeAvatar";

interface PartnerBoardProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament;
  onInviteSent?: () => void;
}

export default function PartnerBoard({
  open,
  onClose,
  tournament,
  onInviteSent,
}: PartnerBoardProps) {
  const { playerToken, playerUser } = useAuth();
  const [entries, setEntries] = useState<PartnerBoardEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<PartnerBoardEntry[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterLevel, setFilterLevel] = useState<number | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sendingInviteTo, setSendingInviteTo] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [detailEntry, setDetailEntry] = useState<PartnerBoardEntry | null>(null);
  const [joinFormOpen, setJoinFormOpen] = useState(false);
  const [joinBio, setJoinBio] = useState("");
  const [joinPreferredLevelMin, setJoinPreferredLevelMin] = useState<number>(1);
  const [joinPreferredLevelMax, setJoinPreferredLevelMax] = useState<number>(5);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (open) {
      fetchPartnerBoard();
    }
  }, [open, tournament.id]);

  useEffect(() => {
    applyFilters();
  }, [entries, filterLevel, searchQuery]);

  const fetchPartnerBoard = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await apiRequest(
        `/api/partner-board?tournamentId=${tournament.id}`,
        {
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
        },
      );
      const data = await response.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || "Failed to load partner board");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinBoard = async () => {
    if (!joinBio.trim()) {
      setError("Please tell us a bit about your playing style");
      return;
    }

    setJoining(true);
    setError("");
    try {
      await apiRequest("/api/partner-board", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          tournamentId: tournament.id,
          bio: joinBio.trim(),
          preferredPartnerLevelMin: joinPreferredLevelMin,
          preferredPartnerLevelMax: joinPreferredLevelMax,
        }),
      });
      setJoinFormOpen(false);
      setJoinBio("");
      await fetchPartnerBoard();
    } catch (err: any) {
      setError(err.message || "Failed to join partner board");
    } finally {
      setJoining(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...entries];

    // Filter by level
    if (filterLevel !== "all") {
      filtered = filtered.filter((entry) => entry.playerLevel === filterLevel);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (entry) =>
          entry.playerName.toLowerCase().includes(query) ||
          entry.bio?.toLowerCase().includes(query),
      );
    }

    setFilteredEntries(filtered);
  };

  const handleSendInvitation = async (entry: PartnerBoardEntry) => {
    setSendingInviteTo(entry.id);
    setError("");

    try {
      const invitationData = {
        tournamentId: tournament.id,
        recipientPlayerId: entry.playerId,
        recipientEmail: entry.playerEmail,
        personalMessage: `Hi! I saw your profile on the Partner Board. Want to team up for ${tournament.name}?`,
      };

      await apiRequest("/api/tournament-invitations", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify(invitationData),
      });

      // Refresh board (this player might be removed if they accepted)
      await fetchPartnerBoard();

      if (onInviteSent) {
        onInviteSent();
      }
    } catch (err: any) {
      setError(err.message || "Failed to send invitation");
    } finally {
      setSendingInviteTo(null);
    }
  };

  const handleRemoveFromBoard = async (entry: PartnerBoardEntry) => {
    setRemoving(true);
    setError("");
    try {
      await apiRequest(`/api/partner-board/${entry.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
      });
      await fetchPartnerBoard();
    } catch (err: any) {
      setError(err.message || "Failed to remove from partner board");
    } finally {
      setRemoving(false);
    }
  };

  const getLevelMatchBadge = (entry: PartnerBoardEntry) => {
    const currentPlayerLevel = playerUser?.playerId
      ? entries.find((e) => e.playerId === playerUser.playerId)?.playerLevel
      : null;

    if (!currentPlayerLevel || !entry.playerLevel) return null;

    const levelDiff = Math.abs(currentPlayerLevel - entry.playerLevel);
    const inPreferredRange =
      entry.preferredPartnerLevelMin &&
      entry.preferredPartnerLevelMax &&
      currentPlayerLevel >= entry.preferredPartnerLevelMin &&
      currentPlayerLevel <= entry.preferredPartnerLevelMax;

    if (levelDiff === 0) {
      return (
        <Chip
          label="Perfect Match!"
          size="small"
          color="success"
          icon={<span>🎯</span>}
        />
      );
    } else if (levelDiff <= 1 && inPreferredRange) {
      return (
        <Chip
          label="Great Match"
          size="small"
          color="primary"
          icon={<span>✨</span>}
        />
      );
    } else if (inPreferredRange) {
      return (
        <Chip
          label="Good Match"
          size="small"
          color="info"
          icon={<span>👍</span>}
        />
      );
    }

    return null;
  };

  const detailIsMe = detailEntry?.playerId === playerUser?.playerId;
  const isOnBoard = entries.some(
    (entry) => entry.playerId === playerUser?.playerId,
  );
  const canJoinBoard = tournament.status === "registration" && !isOnBoard && !loading;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { mx: { xs: 1, sm: "auto" } } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <span style={{ fontSize: "1.5rem" }}>👥</span>
            <Typography variant="h6">Partner Board</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Tournament Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            {tournament.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(tournament.startDate).toLocaleDateString()} •{" "}
            {tournament.venue}
          </Typography>
        </Box>

        {/* Join the board */}
        {canJoinBoard && !joinFormOpen && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<PersonAddIcon />}
            onClick={() => setJoinFormOpen(true)}
            sx={{ mb: 3 }}
          >
            Join This Partner Board
          </Button>
        )}

        {canJoinBoard && joinFormOpen && (
          <Box sx={{ mb: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Join the Partner Board
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tell other players about yourself. They can send you an invitation to team up!
            </Typography>

            <TextField
              fullWidth
              label="About Your Playing Style *"
              multiline
              rows={3}
              value={joinBio}
              onChange={(e) => setJoinBio(e.target.value)}
              placeholder="e.g., Aggressive player, prefer net play, looking for competitive partner"
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 200 }}
              helperText={`${joinBio.length}/200 characters`}
            />

            <Typography variant="body2" gutterBottom>
              Preferred Partner Level
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Min Level</InputLabel>
                <Select
                  value={joinPreferredLevelMin}
                  onChange={(e) => setJoinPreferredLevelMin(Number(e.target.value))}
                  label="Min Level"
                >
                  {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((level) => (
                    <MenuItem key={level} value={level}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Max Level</InputLabel>
                <Select
                  value={joinPreferredLevelMax}
                  onChange={(e) => setJoinPreferredLevelMax(Number(e.target.value))}
                  label="Max Level"
                >
                  {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((level) => (
                    <MenuItem key={level} value={level}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button onClick={() => { setJoinFormOpen(false); setJoinBio(""); }} disabled={joining}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleJoinBoard}
                disabled={joining}
                startIcon={joining ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
              >
                {joining ? "Joining…" : "Join Partner Board"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search players..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <FilterIcon sx={{ mr: 1, color: "action.active" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Level</InputLabel>
                <Select
                  value={filterLevel}
                  onChange={(e) => setFilterLevel(e.target.value as any)}
                  label="Filter by Level"
                >
                  <MenuItem value="all">All Levels</MenuItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                    <MenuItem key={level} value={level}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchPartnerBoard}
                  disabled={loading}
                  sx={{ width: "100%", height: "100%" }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* Player Entries */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredEntries.length === 0 ? (
          <Alert severity="info">
            {entries.length === 0
              ? "No players on the Partner Board yet. Be the first to join!"
              : "No players match your filters. Try adjusting your search."}
          </Alert>
        ) : (
          <>
          {/* Mobile hint */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: "block", sm: "none" }, mb: 1.5, textAlign: "center" }}
          >
            👆 Tap a card to see more info about a player
          </Typography>

          <Grid container spacing={{ xs: 1.5, sm: 3 }}>
            {filteredEntries.map((entry) => {
              const isMe = entry.playerId === playerUser?.playerId;
              return (
                <Grid item xs={6} sm={6} md={4} key={entry.id}>
                  <Card
                    elevation={3}
                    onClick={() => setDetailEntry(entry)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: 2,
                      border: "1px solid",
                      borderColor: isMe ? "primary.main" : "divider",
                      bgcolor: "background.paper",
                      position: "relative",
                      transition: "all 0.2s",
                      height: "100%",
                      "&:hover": { boxShadow: 8, transform: "scale(1.03)" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        pt: { xs: 1.5, sm: 3 },
                        pb: { xs: 1, sm: 2 },
                      }}
                    >
                      <SafeAvatar
                        src={entry.profilePicture}
                        alt={entry.playerName}
                        fallbackText={entry.playerName}
                        sx={{
                          width: { xs: 56, sm: 96 },
                          height: { xs: 56, sm: 96 },
                          mb: 1,
                          border: "3px solid",
                          borderColor: "divider",
                          boxShadow: 2,
                        }}
                      />
                      {isMe && (
                        <Chip label="You" size="small" color="primary" sx={{ mb: 0.5, fontSize: "0.7rem" }} />
                      )}
                      <Typography
                        align="center"
                        sx={{
                          fontWeight: 700,
                          color: "primary.dark",
                          fontSize: { xs: "0.8rem", sm: "1.25rem" },
                          lineHeight: 1.2,
                          px: 0.5,
                        }}
                      >
                        {entry.playerName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Level <b>{entry.playerLevel || "N/A"}</b>
                      </Typography>
                      {entry.isLocked && (
                        <LockIcon
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "error.main",
                            fontSize: { xs: 18, sm: 24 },
                          }}
                        />
                      )}
                      {/* Don't show match badge on own card */}
                      {!isMe && getLevelMatchBadge(entry) && (
                        <Box sx={{ mb: 0.5 }}>{getLevelMatchBadge(entry)}</Box>
                      )}
                    </Box>

                    {/* Bio and level preference — hidden on mobile to keep cards compact */}
                    <CardContent sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 }, display: { xs: "none", sm: "block" } }}>
                      {entry.bio && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            fontSize: "0.95rem",
                            fontStyle: "italic",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: "3.75em",
                          }}
                        >
                          "{entry.bio}"
                        </Typography>
                      )}
                      {entry.waitingForPartner && entry.partnerName && (
                        <Alert severity="info" sx={{ mb: 1, py: 0.5, fontSize: "0.75rem" }}>
                          <Typography variant="caption">
                            ⏳ Waiting for {entry.partnerName}
                          </Typography>
                        </Alert>
                      )}
                      {entry.preferredPartnerLevelMin && entry.preferredPartnerLevelMax && (
                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Looking for:
                          </Typography>
                          <Chip
                            label={`Lvl ${entry.preferredPartnerLevelMin}–${entry.preferredPartnerLevelMax}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                          />
                        </Box>
                      )}
                    </CardContent>

                    <CardActions sx={{ justifyContent: "center", pb: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                      {isMe ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          fullWidth
                          startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                          onClick={(e) => { e.stopPropagation(); handleRemoveFromBoard(entry); }}
                          disabled={removing}
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                        >
                          {removing ? "Removing…" : "Remove Me"}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          startIcon={
                            sendingInviteTo === entry.id
                              ? <CircularProgress size={16} color="inherit" />
                              : <SendIcon />
                          }
                          onClick={(e) => { e.stopPropagation(); handleSendInvitation(entry); }}
                          disabled={sendingInviteTo !== null}
                          sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                        >
                          {sendingInviteTo === entry.id ? "Sending…" : "Invite"}
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>

    {/* Player detail dialog */}
    <Dialog
      open={!!detailEntry}
      onClose={() => setDetailEntry(null)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { mx: { xs: 1, sm: 2 }, borderRadius: 3 } }}
    >
      {detailEntry && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Player Profile</Typography>
              <IconButton size="small" onClick={() => setDetailEntry(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
              <SafeAvatar
                src={detailEntry.profilePicture}
                alt={detailEntry.playerName}
                fallbackText={detailEntry.playerName}
                sx={{ width: 80, height: 80, mb: 1.5, border: "3px solid", borderColor: "divider" }}
              />
              <Typography variant="h6" fontWeight={700} align="center">
                {detailEntry.playerName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Level <b>{detailEntry.playerLevel || "N/A"}</b>
              </Typography>
              {!detailIsMe && getLevelMatchBadge(detailEntry) && (
                <Box mt={1}>{getLevelMatchBadge(detailEntry)}</Box>
              )}
              {detailIsMe && <Chip label="You" size="small" color="primary" sx={{ mt: 1 }} />}
            </Box>

            {detailEntry.bio && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 2 }}>
                "{detailEntry.bio}"
              </Typography>
            )}

            {detailEntry.waitingForPartner && detailEntry.partnerName && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="caption">⏳ Waiting for {detailEntry.partnerName}</Typography>
              </Alert>
            )}

            {detailEntry.preferredPartnerLevelMin && detailEntry.preferredPartnerLevelMax && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="caption" color="text.secondary">Looking for partner:</Typography>
                <Chip
                  label={`Level ${detailEntry.preferredPartnerLevelMin}–${detailEntry.preferredPartnerLevelMax}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={() => setDetailEntry(null)} sx={{ mr: 1 }}>Close</Button>
            {detailIsMe ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={removing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                onClick={() => { handleRemoveFromBoard(detailEntry); setDetailEntry(null); }}
                disabled={removing}
              >
                {removing ? "Removing…" : "Remove Me"}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={sendingInviteTo === detailEntry.id ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                onClick={() => { handleSendInvitation(detailEntry); setDetailEntry(null); }}
                disabled={sendingInviteTo !== null}
              >
                {sendingInviteTo === detailEntry.id ? "Sending…" : "Send Invitation"}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
    </>
  );
}
